import { useDistrict } from "@/lib/district-context";

const WA_NUMBER = "526565271368";

function daysLeft(isoString) {
  if (!isoString) return null;
  const diff = new Date(isoString).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Billing + trial + readonly state derived from the district record.
 *
 * plan              — 'free' | 'pro'
 * isFreePlan        — plan === 'free'
 * trialEndsAt       — ISO string or null
 * trialDaysLeft     — integer days remaining (0 = expired today, null = no trial)
 * isInTrial         — plan === 'free' AND trial still running
 * isTrialExpired    — plan === 'free' AND (trial ended or never existed)
 * isReadonly        — true when writes must be blocked
 * bannerVariant     — 'warning' | 'readonly' | null
 * bannerMessage     — human-readable text (null = no banner)
 * waUpgradeUrl      — pre-filled WhatsApp link with pastor/district name
 * renewalAt         — timestamptz string or null
 * gracePeriodUntil  — timestamptz string or null
 */
export function useReadonly() {
  const { district, userProfile } = useDistrict();

  const plan   = district?.plan ?? "pro";
  const status = district?.subscription_status ?? "active";

  const isFreePlan     = plan === "free";
  const trialEndsAt    = district?.trial_ends_at ?? null;
  const trialDaysLeft  = isFreePlan ? daysLeft(trialEndsAt) : null;
  const isInTrial      = isFreePlan && trialDaysLeft !== null && trialDaysLeft > 0;
  const isTrialExpired = isFreePlan && !isInTrial;

  const isReadonly =
    isTrialExpired ||
    status === "suspended" ||
    status === "cancelled";

  // Compose the WhatsApp message with pastor/district name when available
  const pastorName   = userProfile?.full_name ?? "Pastor";
  const districtName = district?.name ?? "mi distrito";
  const waMsg = encodeURIComponent(
    `Hola Joshua, soy ${pastorName} pastor del Distrito ${districtName}. Quiero activar Plan Pro.`
  );
  const waUpgradeUrl = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;

  let bannerVariant = null;
  let bannerMessage = null;

  if (plan === "pro") {
    if (status === "past_due") {
      bannerVariant = "warning";
      bannerMessage = "Tu suscripción requiere atención — actualiza tu pago para continuar sin interrupciones.";
    } else if (status === "suspended") {
      bannerVariant = "readonly";
      bannerMessage = "Distrito en modo solo lectura — suscripción suspendida.";
    } else if (status === "cancelled") {
      bannerVariant = "readonly";
      bannerMessage = "Suscripción cancelada — acceso de solo lectura activo.";
    }
  } else if (isInTrial) {
    if (trialDaysLeft <= 2) {
      bannerVariant = "warning";
      bannerMessage = `Tu prueba gratuita vence en ${trialDaysLeft} día${trialDaysLeft !== 1 ? "s" : ""}. Contacta a Joshua para continuar.`;
    }
    // 3+ days remaining → no banner (don't interrupt)
  } else {
    // isTrialExpired
    bannerVariant = "readonly";
    bannerMessage = "Tu período de prueba terminó. Contacta a Joshua para activar Plan Pro.";
  }

  return {
    plan,
    isFreePlan,
    trialEndsAt,
    trialDaysLeft,
    isInTrial,
    isTrialExpired,
    isReadonly,
    isPastDue:          status === "past_due",
    subscriptionStatus: status,
    bannerVariant,
    bannerMessage,
    waUpgradeUrl,
    renewalAt:          district?.renewal_at         ?? null,
    gracePeriodUntil:   district?.grace_period_until ?? null,
    billingProfileId:   district?.billing_profile_id ?? null,
  };
}
